/**
 * External Imports
 */
import { FC, useEffect, useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { format } from 'date-fns';

/**
 * Nexus Imports
 */
import {
  NeuCheckbox
  // NeuFeedback,
} from '@neutron/react';

/**
 * Internal Imports
 */
import Table from '../../../shared/table';
import { Dropdown } from '../../../shared/dropdowns';
// import Loading from '../../shared/Loading';

import { RootState } from '../../../../redux/store';

import {
  useLazyGetUnitsConfigQuery,
  usePostUnitLocTypeAndFlagMutation
} from '../../../../redux/apis/Config.api';

/**
 * Global Type Definition Imports
 */
import { Unit } from '../../../../config/interfaces';

/**
 * Styled Imports
 */
import {
  CellContainer,
  StyledLabel,
  StyledNeuTableRow
} from '../../../shared/table/Table.styles';
// import { StyledLabelLoader } from './AreasTable.styles';

const unitTypes = [
  { id: 'I', value: 'Inpatient' },
  { id: 'E', value: 'ED' },
  { id: 'O', value: 'Outpatient' },
  { id: 'BH', value: 'Behavioral Health' }
];

interface IUnitWithOpen extends Unit {
  open: boolean;
}

interface IUnitsTableProps {
  selectedFacility: string;
  searchTerm: string;
}

const UnitsTable: FC<IUnitsTableProps> = ({ selectedFacility, searchTerm }) => {
  const [sortKey, setSortKey] = useState<string>('unit');
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('asc');
  const [active, setActive] = useState<string>('Unit ID');
  const [, setUpdatedUnit] = useState<IUnitWithOpen>();
  const [isUnitTypeDropdownOpen, setIsUnitTypeDropdownOpen] =
    useState<boolean>(false);
  const [isChangingUnitFlag, setIsChangingUnitFlag] = useState<boolean>(false);
  const [openUnitId, setOpenUnitId] = useState<string | null>(null);

  const [
    getUnitsConfig,
    {
      data: unitsConfigData,
      // isError: isUnitsConfigError,
      // isFetching: isUnitsConfigFetching,
      isLoading: isUnitsConfigLoading
    }
  ] = useLazyGetUnitsConfigQuery();
  const [
    postUnitLocTypeAndFlag
    // {
    //   isError: isPostUnitLocTypeAndFlagError,
    //   isLoading: isPostUnitLocTypeAndFlagLoading,
    //   isSuccess: isPostUnitLocTypeAndFlagSuccess
    // }
  ] = usePostUnitLocTypeAndFlagMutation();

  const handleSort = (column: string) => {
    let key;
    switch (column) {
      case 'Unit ID':
        key = 'unitId';
        break;
      case 'Unit Name':
        key = 'unit';
        break;
      case 'Last Updated':
        key = 'lastUpdated';
        break;
      case 'Updated By':
        key = 'userFullName';
        break;
      case 'Unit Type':
        key = 'locType';
        break;
      case 'Active':
        key = 'active';
        break;
      default:
        key = '';
    }
    if (key === sortKey && sortDir === 'asc') {
      setSortDir('desc');
    } else {
      setSortDir('asc');
    }
    setActive(column);
    setSortKey(key);
  };

  const handleChangeLocType = async (value: string, unit: IUnitWithOpen) => {
    if (isUnitTypeDropdownOpen) {
      setIsUnitTypeDropdownOpen(false);
    }
    const { facilityId, unitId, locType } = unit;
    const isActive = unit.active;
    if (locType !== value) {
      const postData = {
        facilityId,
        unitId,
        locType: value,
        active: isActive
      };
      setUpdatedUnit({ ...unit, locType: value });
      await postUnitLocTypeAndFlag(postData);
    }
  };

  const handleChangeFlag = async (checked: boolean, unit: IUnitWithOpen) => {
    const { facilityId, unitId, locType } = unit;
    const isActive = unit.active;
    let flag;
    if (checked) {
      flag = 'Y';
    } else {
      flag = 'N';
    }
    if (flag !== isActive) {
      const postData = {
        facilityId,
        unitId,
        locType,
        active: flag
      };
      setUpdatedUnit({ ...unit, active: flag });
      await postUnitLocTypeAndFlag(postData);
    }
  };
  const filteredUnits = useMemo(() => {
    let allUnits = [...(unitsConfigData ?? [])];
    if (searchTerm) {
      allUnits = allUnits.filter(unit => {
        return (
          unit.unitId.toLowerCase().includes(searchTerm.toLowerCase()) ||
          unit.unit.toLowerCase().includes(searchTerm.toLowerCase())
        );
      });
    }
    // Sorting the list A-Z for initial display
    const sorted = [...allUnits].sort((a: Unit, b: Unit) =>
      a.unit > b.unit ? 1 : -1
    );

    return sorted.map((unit: Unit) => ({ ...unit, open: false }));
  }, [unitsConfigData, searchTerm]);

  const sortedUnits = useMemo(() => {
    const stateCopy = [...filteredUnits];
    const sorted = stateCopy.sort((a: IUnitWithOpen, b: IUnitWithOpen) => {
      if (a[sortKey] === b[sortKey]) {
        return 0;
      }
      if (!a[sortKey]) {
        return sortDir === 'asc' ? 1 : -1;
      }
      if (!b[sortKey]) {
        return sortDir === 'asc' ? -1 : 1;
      }
      if (sortDir === 'asc') {
        return a[sortKey]! > b[sortKey]! ? 1 : -1;
      }
      return a[sortKey]! > b[sortKey]! ? -1 : 1;
    });
    return sorted;
  }, [filteredUnits, sortDir, sortKey]);

  useEffect(() => {
    if (selectedFacility) {
      getUnitsConfig(selectedFacility);
    }
    if (isUnitTypeDropdownOpen) {
      setIsUnitTypeDropdownOpen(false);
    }
    if (isChangingUnitFlag) {
      setIsChangingUnitFlag(false);
    }
  }, [selectedFacility]);

  const headers: JSX.Element[] = [
    <CellContainer
      key="Unit ID"
      id="Units-Table-Unit-Id-Column"
      unit="column"
      colWidth={12}
      direction={!(active === 'Unit ID') ? 'none' : sortDir}
      sort
    >
      Unit ID
    </CellContainer>,
    <CellContainer
      key="Unit Name"
      id="Units-Table-Unit-Name-Column"
      unit="column"
      colWidth={26}
      direction={!(active === 'Unit Name') ? 'none' : sortDir}
      sort
    >
      Unit Name
    </CellContainer>,
    <CellContainer
      key="Last Updated"
      id="Units-Table-Updated-Column"
      unit="column"
      colWidth={16}
      direction={
        !(active === 'Last Updated')
          ? 'none'
          : sortDir === 'asc'
          ? 'desc'
          : 'asc'
      }
      sort
    >
      Last Updated
    </CellContainer>,
    <CellContainer
      key="Updated By"
      id="Units-Table-Updated-By-Column"
      unit="column"
      colWidth={16}
      direction={!(active === 'Updated By') ? 'none' : sortDir}
      sort
    >
      Updated By
    </CellContainer>,
    <CellContainer
      key="Unit Type"
      id="Units-Table-Type-Column"
      unit="column"
      colWidth={20}
      direction={!(active === 'Unit Type') ? 'none' : sortDir}
      sort
    >
      Unit Type
    </CellContainer>,
    <CellContainer
      key="Active"
      id="Units-Table-Active-Column"
      unit="column"
      colWidth={10}
      direction={!(active === 'Active') ? 'none' : sortDir}
      sort
    >
      Active
    </CellContainer>
  ];

  const rows = sortedUnits.map((unit: IUnitWithOpen) => (
    <StyledNeuTableRow
      // eslint-disable-next-line react/no-array-index-key
      key={`Unit-Row-${unit.unitId}`}
      id={`Unit-Row-${unit.unitId}`}
      unit="row"
      size="default"
      height="5em"
    >
      <CellContainer unit="column" colWidth={12}>
        <StyledLabel>{unit.unitId}</StyledLabel>
      </CellContainer>
      <CellContainer unit="column" colWidth={26}>
        <StyledLabel>{unit.unit}</StyledLabel>
      </CellContainer>
      <CellContainer unit="column" colWidth={16}>
        <StyledLabel>
          {unit.lastUpdated
            ? format(new Date(Date.parse(unit.lastUpdated)), 'MM/dd/yyyy')
            : ''}
        </StyledLabel>
      </CellContainer>
      <CellContainer unit="column" colWidth={16}>
        <StyledLabel>
          <p className="my-0">{unit.userFullName || ''}</p>
          <p className="my-0">{unit.lastUpdatedBy || 'Unknown'}</p>
        </StyledLabel>
      </CellContainer>
      <CellContainer unit="column" colWidth={20} styles="overflow: visible;">
        {/* {isChangingUnitFlag &&
          updatedUnit?.unitId === unit.unitId &&
          updatedUnit?.locType !== unit.locType ? (
            <StyledLabelLoader>
              <NeuFeedback kind="spinner" size="small" />
            </StyledLabelLoader>
          ) : ( */}
        <Dropdown
          classes="ps-0"
          name="unitType"
          placeholder={
            unitTypes.find(type => type.id === unit.locType)?.value || ''
          }
          open={openUnitId === unit.unitId}
          styles={{ minWidth: '170px' }}
          setOpen={(bool: boolean) => {
            setIsUnitTypeDropdownOpen(true);
            setOpenUnitId(bool ? unit.unitId : null);
          }}
        >
          {unitTypes &&
            unitTypes.map((item: { id: string; value: string }) => {
              return (
                <div
                  aria-selected={unit.locType === item.id}
                  className={`dropdown-hover-item${
                    unit.locType === item.id ? ' selected' : ''
                  }`}
                  key={item.id}
                  role="option"
                  tabIndex={0}
                  onClick={() => {
                    handleChangeLocType(item.id, unit);
                  }}
                  onKeyDown={() => {
                    handleChangeLocType(item.id, unit);
                  }}
                >
                  {item.value}
                </div>
              );
            })}
        </Dropdown>
        {/* )} */}
      </CellContainer>
      <CellContainer unit="column" colWidth={10}>
        {/* {isChangingUnitFlag &&
          updatedUnit?.unitId === unit.unitId &&
          updatedUnit?.active !== unit.active ? (
            <StyledLabel>
              TODO: Size 18px
              <NeuFeedback kind="spinner" />
            </StyledLabel>
          ) : ( */}
        <StyledLabel>
          <NeuCheckbox
            checked={unit.active === 'Y'}
            color="blue"
            label="Active"
            onNeuChange={e => {
              setIsChangingUnitFlag(true);
              handleChangeFlag(e.detail.checked as boolean, unit);
            }}
          />
        </StyledLabel>
        {/* )} */}
      </CellContainer>
    </StyledNeuTableRow>
  ));

  return (
    <Table
      customClass="admin-areas-unit-table"
      headers={headers}
      rows={rows}
      loading={isUnitsConfigLoading}
      type="areas-units"
      onSort={(e: any) => handleSort(e.detail.label)}
      isAdminAreaUpdatesOnly={isUnitTypeDropdownOpen || isChangingUnitFlag}
    />
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    selectedFacility: state.AdminReducer.selectedFacility
  };
};

export default connect(mapReduxStateToProps)(UnitsTable);
